import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Card,
  CardContent,
  Grid,
  Container,
  Stack,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { functions } from "../firebase";
import Iconify from "../components/Iconify";
import Page from "../components/Page";
import SearchNotFound from "../components/SearchNotFound";
import AddPlanForm from "./AddPlan";

// Helpers
const CADENCE_ORDER = { WEEKLY: 1, MONTHLY: 2, ANNUAL: 3 };
const CADENCE_LABELS = {
  NINETY_DAYS: "One Payment",
  DAILY: "Daily Payments",
  WEEKLY: "Weekly Payments",
  MONTHLY: "Monthly Payments",
  ANNUAL: "One Payment",
};

function getDurationUnit(cadence) {
  switch (cadence) {
    case "DAILY": return "Days";
    case "WEEKLY": return "Weeks";
    case "MONTHLY": return "Months";
    case "ANNUAL": return "Years";
    case "NINETY_DAYS": return "Season (90 Days)";
    default: return "";
  }
}

function extractPlanDetails(variation, plan) {
  const phase = variation.subscription_plan_variation_data?.phases?.[0] || {};
  const price = Number(phase.pricing?.price?.amount || 0);
  const currency = phase.pricing?.price?.currency || "AUD";
  const duration = Number(phase.periods) || 0;
  const cadence = phase.cadence || "";
  const rawName = variation.subscription_plan_variation_data?.name || "";
  const cleanName = rawName.replace("Variation", "").trim();
  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(price / 100);
  let totalAmount = "A$0.00";
  if (price > 0 && duration > 0) {
    totalAmount = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format((price * duration) / 100);
  }
  const durationLabel = duration > 0
    ? `${duration} ${getDurationUnit(cadence)}`
    : "Forever";

  // type/category
  let planType = "Other";
  if (cleanName.toLowerCase().includes("annual")) planType = "Annual";
  if (cleanName.toLowerCase().includes("season")) planType = "Seasonal";
  let planCategory = "Adult";
  if (cleanName.toLowerCase().includes("junior")) planCategory = "Junior";

  return {
    planId: plan.id,
    variationId: variation.id,
    name: cleanName || "N/A",
    formattedPrice,
    paymentFrequency: cadence,
    duration: durationLabel,
    totalAmount,
    planType,
    planCategory,
  };
}

export default function Plans() {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [filterName, setFilterName] = useState("");
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [planTypeFilter, setPlanTypeFilter] = useState("Annual");
  const [planCategoryFilter, setPlanCategoryFilter] = useState("Adult");

  // fetch & flatten
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "fetchSquarePlans");
      const { data } = await fn();
      const flat = (data.plans || [])
        .filter(p => !p.is_deleted && p.present_at_all_locations)
        .flatMap(plan =>
          (plan.subscription_plan_data?.subscription_plan_variations || [])
            .filter(v => !v.is_deleted)
            .map(v => extractPlanDetails(v, plan))
        );
      setAllPlans(flat);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // delete single variant
  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this plan variation?")) return;
    setDeletingId(planId);
    try {
      const delFn = httpsCallable(functions, "deleteSquarePlan");
      await delFn({ planId });
      await fetchPlans();
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  // filter by text / type / category
  const filterPlans = () => {
    let filtered = allPlans.filter(p =>
      p.name.toLowerCase().includes(filterName.toLowerCase())
    );
    if (planTypeFilter === "Sponsor") {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes("sponsor") ||
        p.name.toLowerCase().includes("life")
      );
    } else {
      filtered = filtered.filter(p =>
        !p.name.toLowerCase().includes("sponsor") &&
        !p.name.toLowerCase().includes("life") &&
        p.planType === planTypeFilter
      );
    }
    return filtered.filter(p => p.planCategory === planCategoryFilter);
  };

  // group & sort
  const getGrouped = () => {
    const filtered = filterPlans();
    const groups = {};
    filtered.forEach(p => {
      (groups[p.name] = groups[p.name] || []).push(p);
    });
    // sort group names: unlimited first
    const entries = Object.entries(groups).sort(([aName, aVars], [bName, bVars]) => {
      const aUn = aVars.some(v => v.name.toLowerCase().includes("multi"));
      const bUn = bVars.some(v => v.name.toLowerCase().includes("multi"));
      if (aUn && !bUn) return -1;
      if (!aUn && bUn) return 1;
      return aName.localeCompare(bName);
    });
    // within each, sort by cadence
    return entries.map(([name, vars]) => ({
      name,
      variants: vars.slice().sort((a, b) =>
        (CADENCE_ORDER[a.paymentFrequency] ?? 99)
        - (CADENCE_ORDER[b.paymentFrequency] ?? 99)
      )
    }));
  };

  // render cards grouped
  const renderCards = () => {
    const groups = getGrouped();
    if (!groups.length) {
      return <Typography>No plans match your filters.</Typography>;
    }
    return (
      <Grid container spacing={3}>
        {groups.map(({ name, variants }) => (
          <Grid item xs={12} md={6} lg={4} key={name}>
            <Card>
              <CardContent>
                <Typography variant="h6">{name}</Typography>
                <Divider sx={{ my: 1 }} />
                {variants.map(v => (
                  <Box
                    key={v.variationId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        pb: 2,
                        borderBottom: "1px solid #eee",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {(CADENCE_LABELS[v.paymentFrequency] || v.paymentFrequency).toUpperCase()}:{" "}
                        <Box component="span" sx={{ color: "primary.main", fontWeight: "bold" }}>
                          {v.formattedPrice}
                        </Box>
                      </Typography>

                      <Typography variant="body2" gutterBottom>
                        Duration: {v.duration}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Total Cost:{" "}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                          {v.totalAmount}
                        </Box>
                      </Typography>
                    </Box>

                    <IconButton
                      color="error"
                      onClick={() => handleDelete(v.planId)}
                      disabled={deletingId === v.planId}
                    >
                      {deletingId === v.planId
                        ? <CircularProgress size={20} />
                        : <Iconify icon="eva:trash-2-outline" />}
                    </IconButton>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Page title="Subscription Plans">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          spacing={2}
        >
          <Typography variant="h4">Plans</Typography>
          <TextField
            size="small"
            placeholder="Search…"
            value={filterName}
            onChange={e => setFilterName(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => {
              setEditData(null);
              setPlanFormOpen(true);
            }}
          >
            New Plan
          </Button>
        </Stack>

        {planFormOpen && (
          <AddPlanForm
            initialData={editData}
            onSave={() => { fetchPlans(); setPlanFormOpen(false); }}
            onClose={() => setPlanFormOpen(false)}
          />
        )}

        <Divider sx={{ mb: 3 }} />

        <ToggleButtonGroup
          value={planTypeFilter}
          exclusive
          onChange={(e, v) => v && setPlanTypeFilter(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="Annual">Annual</ToggleButton>
          <ToggleButton value="Seasonal">Seasonal</ToggleButton>
          <ToggleButton value="Other">Other</ToggleButton>
          <ToggleButton value="Sponsor">Sponsor/Life</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={planCategoryFilter}
          exclusive
          onChange={(e, v) => v && setPlanCategoryFilter(v)}
          sx={{ mb: 4, ml: 2 }}
        >
          <ToggleButton value="Adult">Adult</ToggleButton>
          <ToggleButton value="Junior">Junior</ToggleButton>
        </ToggleButtonGroup>

        {loading
          ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          )
          : renderCards()
        }
      </Container>
    </Page>
  );
}

Plans.propTypes = {
  // no external props needed
};
